import { basicAuth, customAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { escapeRegexLiteral, exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const apiBase = (apiHost: string) => `https://${apiHost}/api`;
const defaultUrlPattern = ({ apiHost }: Record<string, string>) =>
  `^${escapeRegexLiteral(`${apiBase(apiHost || "api.adaptiveplanning.com")}/v40`)}$`;
const jsonUrlPattern = ({ apiHost }: Record<string, string>) =>
  `^${exactUrlToRegex(`${apiBase(apiHost || "api.adaptiveplanning.com")}/rest`)}$`;

const hostStep = {
  title: "Enter your Adaptive Planning API host",
  items: [
    {
      kind: "hostname" as const,
      label: "API hostname",
      target: { kind: "configuration" as const, field: "apiHost" },
      placeholder: "api.adaptiveplanning.com",
      defaultValue: "api.adaptiveplanning.com",
      required: true,
      helperText:
        "Use the API hostname for your Adaptive Planning cluster and environment.",
    },
  ],
} as const;

const credentialsStep = {
  title: "Enter your Adaptive Planning credentials",
  items: [
    {
      kind: "input" as const,
      label: "Login ID",
      target: { kind: "secrets" as const, field: "login" },
      required: true,
    },
    {
      kind: "input" as const,
      label: "Password",
      target: { kind: "secrets" as const, field: "password" },
      required: true,
    },
  ],
} as const;

export const adaptivePlanning = defineProduct({
  name: "Adaptive Planning",
  tagLine: "Planning and financial modeling",
  logo,
  tags: ["data"],
  variants: [
    productVariant({
      identifier: "adaptivePlanning",
      authLabel: "XML API",
      protocol: "http",
      defaultUrlPattern,
      auth: customAuth("adaptivePlanningXml"),
      steps: [
        hostStep,
        {
          title: "Use the XML API",
          items: [
            {
              kind: "markdown",
              content:
                "The XML API accepts POST requests at `/api/v40`. Include a single `<credentials/>` element in each `<call>` body; the connector fills in the login ID and password before sending the request. This option requires native Adaptive Planning credentials; Workday-synchronized users require OAuth.",
            },
          ],
        },
        credentialsStep,
      ],
      connectionTest: {
        protocol: "http",
        request: ({ apiHost }) => ({
          url: `${apiBase(apiHost)}/v40`,
          method: "POST",
          headers: { "Content-Type": "text/xml" },
          body: '<call method="exportVersions" callerName="3B"><credentials/></call>',
          expectedBodyContains: 'success="true"',
        }),
      },
      usageHint: ({ apiHost }) =>
        `POST XML calls to ${apiBase(apiHost)}/v40, with one <credentials/> element for automatic credential injection.`,
    }),
    productVariant({
      identifier: "adaptivePlanningJson",
      authLabel: "JSON REST API",
      protocol: "http",
      defaultUrlPattern: jsonUrlPattern,
      auth: (app) =>
        basicAuth({
          username: app.secret.login,
          password: app.secret.password,
        }),
      steps: [
        hostStep,
        {
          title: "Use the JSON REST API",
          items: [
            {
              kind: "markdown",
              content:
                "The JSON REST APIs use Basic authentication in the Authorization header. They cover selected modeling and security operations. This option requires native Adaptive Planning credentials; Workday-synchronized users require OAuth.",
            },
          ],
        },
        credentialsStep,
      ],
      connectionTest: {
        protocol: "http",
        request: ({ apiHost }) => ({
          url: `${apiBase(apiHost)}/rest/modeling/v1/default/versions`,
          expectedStatus: [200, 403],
        }),
      },
      usageHint: ({ apiHost }) =>
        `Use JSON REST endpoints under ${apiBase(apiHost)}/rest/. Authentication is applied automatically.`,
    }),
  ],
});
